// /series/:seriesid/:episodeid — episode detail: audio + shownotes + the chapter body. Loader-only ⇒ pure SSG (see series.tsx). Node-prerender-safe: fetch + MUI + local helpers only.
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { SplashChrome } from './splash-chrome';
import SeriesCrumbs from './series-crumbs';
import Markdown from './series-markdown';
import { fetchEpisode, formatDuration, numberedEpisodeTitle, type ShowDetail, type ShowEpisode } from './series-data';
import { metaDescription, breadcrumbLdJson, buildPodcastEpisodeLd, podcastTitle, episodeUrl } from './series-ld';
import SeriesNotFound, { NOT_FOUND_META } from './series-notfound';
import type { Route } from './+types/series.$seriesid.$episodeid';

type LoaderData = { show: ShowDetail; episode: ShowEpisode } | null;

export async function loader({ params }: Route.LoaderArgs): Promise<LoaderData> {
  // See series.$seriesid.tsx's loader: a genuine miss returns null without throwing, so this catch only fires on a broken artifact fetch — silently at prerender.
  try { return await fetchEpisode(params.seriesid, params.episodeid); }
  catch (e) { console.warn('[series] episode fetch failed:', params.seriesid, params.episodeid, e); return null; }
}

// Episode deep links inherit the show's fate — see series.$seriesid.tsx for why a non-prerendered path's server-loader fetch throws, and why this catch is what keeps /podcaster/:slug/:id landing somewhere honest instead of on RR's error screen.
export async function clientLoader({ serverLoader }: Route.ClientLoaderArgs) {
  try { return await serverLoader(); }
  catch { return null; }
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
  if (!loaderData) return NOT_FOUND_META;
  const { show, episode } = loaderData;
  const episodeName = numberedEpisodeTitle(episode);
  const title = `${episodeName} — ${podcastTitle(show.title)}`;
  const description = metaDescription(episode.teaser ?? episode.shownotes);
  const url = episodeUrl(show.slug, episode.id);
  const tags = [
    { title }, { name: 'description', content: description },
    { property: 'og:title', content: title }, { property: 'og:description', content: description },
    { property: 'og:image', content: show.cover_url },
    { property: 'og:url', content: url }, { tagName: 'link', rel: 'canonical', href: url },
  ];
  // Same noindex gate as the show page (D3): a non-`featured` show's episodes stay unindexed too.
  if (show.privacy !== 'featured') return [...tags, { name: 'robots', content: 'noindex,follow' }];
  return [
    ...tags,
    { 'script:ld+json': buildPodcastEpisodeLd(show, episode) },
    // Mirrors the VISIBLE `SeriesCrumbs` below hop-for-hop (see series.$seriesid.tsx's copy of this).
    { 'script:ld+json': breadcrumbLdJson([
      { name: 'Series', path: '/series' },
      { name: show.title, path: `/series/${show.slug}` },
      { name: episodeName, path: `/series/${show.slug}/${episode.id}` },
    ]) },
  ];
};

export default function SeriesEpisode({ loaderData }: Route.ComponentProps) {
  if (!loaderData) return <SeriesNotFound />;

  const { show, episode } = loaderData;
  const duration = formatDuration(episode.final_duration_seconds);
  const title = numberedEpisodeTitle(episode);

  return (
    // SplashChrome wears `Mui` itself — never nest a second ThemeProvider here.
    <SplashChrome active="series">
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={2}>
          {/* Supersedes the bare show-title link that used to sit here: same hop up, plus the one to the directory a deep-linked arrival had no way to reach. */}
          <SeriesCrumbs items={[
            { label: 'Series', to: '/series#featured' },
            { label: show.title, to: `/series/${show.slug}` },
            { label: title },
          ]} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>{title}</Typography>
          {duration && <Typography color="text.secondary">{duration}</Typography>}
          {episode.audio_url ? (
            <Box component="audio" controls src={episode.audio_url} sx={{ width: '100%' }} />
          ) : (
            <Typography color="text.secondary">Audio is still processing.</Typography>
          )}
          {episode.shownotes && <Markdown>{episode.shownotes}</Markdown>}
          {/* The body is the chapter narration (`entries.text`, chapter rows only). Absent on every pre-`text` artifact and every non-chapter episode — those render no rule either, never an empty block. */}
          {episode.text && <><Divider /><Markdown>{episode.text}</Markdown></>}
        </Stack>
      </Container>
    </SplashChrome>
  );
}
